/**
 * Listing preview card: pictures carousel + editable key-value table + attributes table.
 */
import React from 'react';
import { Card, Table } from 'react-bootstrap';
import { EditableCell } from '../common';
import ListingPictureCarousel from './ListingPictureCarousel';

const MAIN_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'category_id', label: 'Category ID' },
  { key: 'condition', label: 'Condition' },
  { key: 'price', label: 'Price' },
  { key: 'currency_id', label: 'Currency' },
  { key: 'available_quantity', label: 'Available quantity', readOnly: true },
  { key: 'buying_mode', label: 'Buying mode' },
  { key: 'listing_type_id', label: 'Listing type' },
  { key: 'sale_terms', label: 'Sale terms', getDisplayValue: (listing) => listing.sale_terms?.length ? listing.sale_terms.map((t) => t.value_name).join(', ') : '' }
];

/**
 * @param {{
 *   effectiveListing: { title: string, category_id: string, condition: string, price: string, currency_id: string, available_quantity: string, buying_mode: string, listing_type_id: string, sale_terms: Array<{ value_name: string }>, pictures: Array<unknown>, attributes: Array<{ id: string, value_name: string }> },
 *   listingPictures: Array<{ source: string }>,
 *   listingPictureIndex: number,
 *   setListingPictureIndex: (fn: (i: number) => number) => void,
 *   editingField: string | null,
 *   editingValue: string,
 *   setEditingValue: (v: string) => void,
 *   handleStartEdit: (key: string, value: unknown) => void,
 *   handleSaveEdit: () => void,
 *   headerAction?: React.ReactNode
 * }} props
 */
const ListingPreviewCard = ({
  effectiveListing,
  listingPictures,
  listingPictureIndex,
  setListingPictureIndex,
  editingField,
  editingValue,
  setEditingValue,
  handleStartEdit,
  handleSaveEdit,
  headerAction
}) => {
  if (!effectiveListing) return null;

  const goPrev = () => setListingPictureIndex((i) => (i <= 0 ? listingPictures.length - 1 : i - 1));
  const goNext = () => setListingPictureIndex((i) => (i >= listingPictures.length - 1 ? 0 : i + 1));

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span>Listing preview (Mercado Livre)</span>
        {headerAction}
      </Card.Header>
      <Card.Body className="p-0">
        {listingPictures.length > 0 ? (
          <ListingPictureCarousel
            pictures={listingPictures}
            currentIndex={listingPictureIndex}
            onPrevious={goPrev}
            onNext={goNext}
          />
        ) : (
          <p className="small text-muted mb-2 px-3 pt-3">No pictures for this product.</p>
        )}
        <p className="small text-muted mb-2 px-3 pt-2">Click a value to edit; your input is used when publishing. Available quantity is set in Pick listing suppliers.</p>
        <Table className="info-table mb-0" size="sm" responsive>
          <tbody>
            {MAIN_FIELDS.map(({ key, label, getDisplayValue, readOnly }) => {
              const displayValue = getDisplayValue ? getDisplayValue(effectiveListing) : effectiveListing[key];
              return (
                <tr key={key}>
                  <td className="info-table-label">{label}</td>
                  <td className="info-table-value">
                    {readOnly ? (
                      <span>{displayValue ?? '—'}</span>
                    ) : (
                      <EditableCell
                        fieldKey={key}
                        displayValue={displayValue}
                        isEditing={editingField === key}
                        editingValue={editingValue}
                        onEditingValueChange={setEditingValue}
                        onStartEdit={handleStartEdit}
                        onSaveEdit={handleSaveEdit}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td className="info-table-label">Pictures</td>
              <td className="info-table-value">
                {effectiveListing.pictures.length === 0 ? 'None' : `${effectiveListing.pictures.length} image(s)`}
              </td>
            </tr>
          </tbody>
        </Table>
        {effectiveListing.attributes?.length > 0 && (
          <>
            <hr className="my-2 mx-3 border-secondary" />
            <p className="small text-muted mb-1 px-3 pt-2">Attributes</p>
            <Table className="info-table mb-0" size="sm" responsive>
              <thead>
                <tr>
                  <th className="info-table-label">ID</th>
                  <th className="info-table-label">Value</th>
                </tr>
              </thead>
              <tbody>
                {effectiveListing.attributes.map((a, idx) => (
                  <tr key={a.id + String(idx)}>
                    <td className="info-table-value">{a.id}</td>
                    <td className="info-table-value">
                      <EditableCell
                        fieldKey={`attr_${a.id}`}
                        displayValue={a.value_name}
                        isEditing={editingField === `attr_${a.id}`}
                        editingValue={editingValue}
                        onEditingValueChange={setEditingValue}
                        onStartEdit={handleStartEdit}
                        onSaveEdit={handleSaveEdit}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ListingPreviewCard;
